/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LanguageCode, TRANSLATIONS } from '../translations';
import { auth } from '../firebase';
import {
  Package,
  Sparkles,
  ArrowLeft,
  ShieldCheck,
  Lock,
  TrendingUp,
  Compass,
  Layers,
  CheckCircle,
  ChevronRight,
  Info
} from 'lucide-react';

export interface ShopBundle {
  id: string;
  name: string;
  packs: number;
  stickersCount: number;
  bonusText: string;
  price: string;
  priceNum: number;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  glowColor: string;
  badge?: string;
}

const BUNDLES: ShopBundle[] = [
  {
    id: 'starter',
    name: 'Starter Bundle',
    packs: 2,
    stickersCount: 10,
    bonusText: '—',
    price: '1.99€',
    priceNum: 1.99,
    color: 'sky',
    gradientFrom: 'from-sky-500',
    gradientTo: 'to-blue-600',
    glowColor: 'rgba(14, 165, 233, 0.15)',
    badge: 'Beginner Choice'
  },
  {
    id: 'fan',
    name: 'Fan Bundle',
    packs: 6, // 5 packs + 1 pack bonus = 6 packs total
    stickersCount: 25,
    bonusText: '+1 pack',
    price: '4.49€',
    priceNum: 4.49,
    color: 'emerald',
    gradientFrom: 'from-emerald-400',
    gradientTo: 'to-teal-600',
    glowColor: 'rgba(16, 185, 129, 0.2)',
    badge: 'Popular +1 Free'
  },
  {
    id: 'collector',
    name: 'Collector Bundle',
    packs: 12, // 10 packs + 2 packs bonus = 12 packs total
    stickersCount: 50,
    bonusText: '+2 packs',
    price: '8.49€',
    priceNum: 8.49,
    color: 'violet',
    gradientFrom: 'from-violet-500',
    gradientTo: 'to-purple-700',
    glowColor: 'rgba(139, 92, 246, 0.25)',
    badge: 'Best Value (+2 Free)'
  },
  {
    id: 'mega',
    name: 'Mega Box',
    packs: 25, // 20 packs + 5 packs bonus = 25 packs total
    stickersCount: 100,
    bonusText: '+5 packs',
    price: '15.99€',
    priceNum: 15.99,
    color: 'amber',
    gradientFrom: 'from-amber-400',
    gradientTo: 'to-orange-600',
    glowColor: 'rgba(245, 158, 11, 0.3)',
    badge: 'Mega Offer (+5 Free)'
  },
  {
    id: 'ultimate',
    name: 'Ultimate Box',
    packs: 65, // 50 packs + 15 packs bonus = 65 packs total
    stickersCount: 250,
    bonusText: '+15 packs',
    price: '36.99€',
    priceNum: 36.99,
    color: 'rose',
    gradientFrom: 'from-rose-500',
    gradientTo: 'to-pink-600',
    glowColor: 'rgba(244, 63, 94, 0.35)',
    badge: 'Whale Saver (+15 Free)'
  }
];

// Public PayPal client-id for loading the JS SDK in the browser. Sourced from
// env per build: VITE_PAYPAL_CLIENT_ID (sandbox in .env.development.local, live
// in .env.production.local). The matching secret lives in the Cloud Function.
const PAYPAL_CLIENT_ID = ((import.meta as any).env?.VITE_PAYPAL_CLIENT_ID) || 'AZ2K4GxGl0R4Cs9p2AQAf9Qtd_4zMPR_eMacjoIsH1MN0e5Fwjg9cvsSUaiv-5OUhJAMfWwXpRX2S-BW';

// Base URL for the PayPal Cloud Function. Empty in production (same-origin via
// the Hosting rewrite). For local `npm run dev`, set VITE_PAYPAL_API_BASE to the
// deployed function URL so the browser calls the online function directly.
const PAYPAL_API_BASE = ((import.meta as any).env?.VITE_PAYPAL_API_BASE) || '';

// Calls a /api/paypal/* endpoint with the current user's Firebase ID token.
async function callPaypalApi(path: string, body: Record<string, unknown>) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('You must be signed in to make a purchase.');
  const token = await currentUser.getIdToken();
  const res = await fetch(`${PAYPAL_API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Request to ${path} failed (${res.status}).`);
  }
  return data;
}

interface PayPalButtonWrapperProps {
  bundle: ShopBundle;
  onSuccess: (bundle: ShopBundle) => void;
  onPurchaseComplete: (newPackTotal: number) => void;
  onError: (errMessage: string) => void;
  onProcessing: (status: string) => void;
  lang?: LanguageCode;
}

const PayPalButtonWrapper = ({
  bundle,
  onSuccess,
  onPurchaseComplete,
  onError,
  onProcessing,
  lang
}: PayPalButtonWrapperProps) => {
  const t = (key: string) => {
    const activeLang = lang || 'en';
    return TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || key;
  };

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [sdkLoaded, setSdkLoaded] = useState(typeof (window as any).paypal !== 'undefined');

  useEffect(() => {
    let interval: any;
    if (!(window as any).paypal) {
      interval = setInterval(() => {
        if ((window as any).paypal) {
          setSdkLoaded(true);
          clearInterval(interval);
        }
      }, 300);
    } else {
      setSdkLoaded(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!sdkLoaded || !containerRef.current) return;

    // Clear any existing children of the container ref before rendering
    containerRef.current.innerHTML = '';

    let buttonsInstance: any = null;
    let isCancelled = false;

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (reason) {
        const errorMsg = (reason.message || String(reason)).toLowerCase();
        if (
          errorMsg.includes('window closed') ||
          errorMsg.includes('closed before response') ||
          errorMsg.includes('close_window')
        ) {
          event.preventDefault();
          console.warn('Suppressed asynchronous unhandled rejection from closed PayPal popup:', reason);
          if (!isCancelled) {
            onError('The payment window was closed before completing the purchase.');
          }
        }
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);

    try {
      buttonsInstance = (window as any).paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal'
        },
        createOrder: async () => {
          // The server owns the price; we only send the bundle id.
          const data = await callPaypalApi('/api/paypal/create-order', {
            bundleId: bundle.id,
          });
          return data.orderID;
        },
        onApprove: async (data: any) => {
          // NOTE: switching to the processing step unmounts this component, so
          // we must NOT gate the capture result on `isCancelled` — the result
          // callbacks update the parent StickerShop, which stays mounted.
          onProcessing('Securing gateway tokens from PayPal node...');
          try {
            onProcessing('Synchronizing purchase records with secure cloud ledger...');
            // The server captures, verifies the amount, and credits packs.
            const result = await callPaypalApi('/api/paypal/capture-order', {
              orderID: data.orderID,
            });
            if (!result?.success || typeof result.newPackTotal !== 'number') {
              throw new Error('Payment could not be verified.');
            }
            onPurchaseComplete(result.newPackTotal);
            onSuccess(bundle);
          } catch (err: any) {
            const errMsg = (err?.message || String(err)).toLowerCase();
            const isWindowClosed = errMsg.includes('window closed') || errMsg.includes('closed before response') || errMsg.includes('close_window');
            if (isWindowClosed) {
              console.warn('PayPal Window closed early: ', err);
              onError('The payment window was closed before completing the purchase.');
            } else {
              console.error('PayPal Order Capture Error: ', err);
              onError(err.message || 'Verification capture process failed');
            }
          }
        },
        onCancel: (data: any) => {
          console.log('PayPal checkout cancelled: ', data);
          if (isCancelled) return;
          onError('The payment window was closed or the transaction was cancelled.');
        },
        onError: (err: any) => {
          if (isCancelled) return;
          const errMsg = (err?.message || String(err)).toLowerCase();
          const isWindowClosed = errMsg.includes('window closed') || errMsg.includes('closed before response') || errMsg.includes('close_window');
          if (isWindowClosed) {
            console.warn('PayPal Core SDK warning (Window closed before response):', err);
            onError('The payment window was closed before completing the purchase.');
          } else {
            console.error('PayPal core SDK error: ', err);
            onError('The transaction handshake was cancelled or did not complete.');
          }
        }
      });

      if (buttonsInstance && buttonsInstance.isEligible()) {
        buttonsInstance.render(containerRef.current);
      }
    } catch (e: any) {
      console.error('Error rendering PayPal buttons: ', e);
    }

    return () => {
      isCancelled = true;
      window.removeEventListener('unhandledrejection', handleRejection);
      if (buttonsInstance) {
        try {
          buttonsInstance.close().catch((err: any) => {
            console.warn('Silent issue closing PayPal buttons:', err);
          });
        } catch (err) {
          console.warn('Failed to call close on PayPal buttons:', err);
        }
      }
    };
  }, [sdkLoaded, bundle]);

  return (
    <div className="space-y-3">
      {!sdkLoaded ? (
        <div className="flex flex-col items-center justify-center p-6 bg-slate-900/60 border border-slate-800 rounded-lg">
          <div className="w-5 h-5 border-2 border-slate-600 border-t-yellow-400 rounded-full animate-spin mb-2" />
          <span className="text-[10px] text-slate-500 uppercase tracking-widest animate-pulse">{t('paypalGateway')}</span>
        </div>
      ) : null}
      <div ref={containerRef} id="paypal-button-container" className="min-h-[150px] overflow-hidden" />
    </div>
  );
};

interface StickerShopProps {
  onBack: () => void;
  onAddPacks: (amount: number) => Promise<void>;
  onPurchaseComplete: (newPackTotal: number) => void;
  packs: number;
  lang?: LanguageCode;
}

export const StickerShop = ({ onBack, onAddPacks, onPurchaseComplete, packs, lang }: StickerShopProps) => {
  const t = (key: string) => {
    const activeLang = lang || 'en';
    return TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || key;
  };

  const getBundleName = (id: string, defaultName: string) => {
    const key = `${id}Bundle`;
    const activeLang = lang || 'en';
    const dict = TRANSLATIONS[activeLang] || TRANSLATIONS['en'];
    if (id === 'mega') return dict['megaBox'] || defaultName;
    if (id === 'ultimate') return dict['ultimateBox'] || defaultName;
    return dict[key] || defaultName;
  };

  const getBundleBadge = (id: string, defaultBadge?: string) => {
    if (!defaultBadge) return undefined;
    const activeLang = lang || 'en';
    const dict = TRANSLATIONS[activeLang] || TRANSLATIONS['en'];
    if (id === 'starter') return dict['beginnerChoice'] || defaultBadge;
    if (id === 'fan') return dict['popularBonus'] || defaultBadge;
    if (id === 'collector') return dict['bestValue'] || defaultBadge;
    if (id === 'mega') return dict['megaOffer'] || defaultBadge;
    if (id === 'ultimate') return dict['whaleSaver'] || defaultBadge;
    return defaultBadge;
  };

  const [selectedBundle, setSelectedBundle] = useState<ShopBundle | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'bitcoin'>('paypal');
  const [checkoutStep, setCheckoutStep] = useState<'selection' | 'form' | 'processing' | 'success'>('selection');
  const [processStatus, setProcessStatus] = useState('');
  const [savedSuccessBundle, setSavedSuccessBundle] = useState<ShopBundle | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Crypto / Bitcoin specific payment states
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [bitcoinTransferMode, setBitcoinTransferMode] = useState<'nowpayments' | 'bitpay' | 'qr' | 'wallet'>('nowpayments');
  const [connectingWallet, setConnectingWallet] = useState(false);

  // NOWPayments specific payment states
  const [nowpaymentsInvoiceId, setNowpaymentsInvoiceId] = useState<string | null>(null);
  const [nowpaymentsInvoiceUrl, setNowpaymentsInvoiceUrl] = useState<string | null>(null);
  const [nowpaymentsStatus, setNowpaymentsStatus] = useState<'idle' | 'created' | 'verifying' | 'failed' | 'success'>('idle');
  const [nowpaymentsLoading, setNowpaymentsLoading] = useState(false);

  // Load standard PayPal SDK script as soon as shop pages mount
  React.useEffect(() => {
    const scriptId = 'paypal-sdk-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      try {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=EUR`;
        script.async = true;
        script.setAttribute('crossorigin', 'anonymous');
        script.onload = () => {
          console.log('PayPal JS SDK has initialized successfully in background.');
        };
        script.onerror = (e) => {
          console.error('Critical failed error loading secure PayPal JS SDK:', e);
        };
        document.body.appendChild(script);
      } catch (err) {
        console.error('Failed to append PayPal JS SDK script:', err);
      }
    }
  }, []);

  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBundle) return;

    setSavedSuccessBundle(selectedBundle);
    setCheckoutStep('processing');

    const btcStepsTranslations: Record<string, string[]> = {
      en: [
        'Establishing secure node handshake with Bitcoin Mainnet peer arrays...',
        'Locating cryptographic transaction block matching required satoshi threshold...',
        'Waiting for miner broadcast confirmation on decentralized blockchain Ledger...',
        'Signature validated successfully! Authenticating NFT token registration certificate...'
      ],
      es: [
        'Estableciendo enlace de nodo seguro con arreglos de pares de Bitcoin Mainnet...',
        'Localizando bloque de transacción criptográfica que coincida con el umbral de satoshi requerido...',
        'Esperando confirmación de transmisión del minero en el libro diario descentralizado...',
        '¡Firma validada con éxito! Autenticando certificado de registro de token NFT...'
      ],
      bs: [
        'Uspostavljanje sigurne veze čvora sa Bitcoin Mainnet nizovima peer-ova...',
        'Pronalaženje kriptografskog transakcijskog bloka koji odgovara traženom satoshi pragu...',
        'Čekanje potvrde emitovanja rudara na decentralizovanom blockchain registru...',
        'Potpis uspješno validiran! Autentifikacija NFT certifikata o registraciji tokena...',
      ],
      ro: [
        'Se stabilește conexiunea securizată a nodului cu rețelele de conectori Bitcoin Mainnet...',
        'Se caută blocul de tranzacție criptografică care corespunde pragului satoshi cerut...',
        'Se așteaptă confirmarea difuzării minerului în registrul descentralizat blockchain...',
        'Semnătură validată cu succes! Se autentifică certificatul de înregistrare a jetonului NFT...'
      ]
    };

    const activeLang = lang || 'en';
    const steps = btcStepsTranslations[activeLang] || btcStepsTranslations['en'];

    for (let i = 0; i < steps.length; i++) {
      setProcessStatus(steps[i]);
      await new Promise(resolve => setTimeout(resolve, 900));
    }

    // Grant packs to user state (which triggers automated Firestore/localStorage syncing)
    await onAddPacks(selectedBundle.packs);

    setCheckoutStep('success');
  };

  const resetCheckout = () => {
    setSelectedBundle(null);
    setCheckoutStep('selection');
    setProcessStatus('');
    setCopiedAddress(false);
    setWalletConnected(false);
    setBitcoinTransferMode('nowpayments');
    setConnectingWallet(false);
    setCheckoutError(null);
    setNowpaymentsInvoiceId(null);
    setNowpaymentsInvoiceUrl(null);
    setNowpaymentsStatus('idle');
    setNowpaymentsLoading(false);
  };

  return (
    <div className="w-full text-slate-300 font-sans select-none relative pb-16">
      <div className="absolute top-0 left-0 w-full h-96 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-1/4 w-[50%] h-[300px] rounded-full bg-blue-600 blur-[150px]" />
        <div className="absolute top-20 right-1/4 w-[40%] h-[200px] rounded-full bg-purple-600 blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Breadcrumb Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-colors self-start border border-slate-800/80 bg-slate-900/60 hover:bg-slate-800/80 px-3 py-1.5 rounded-lg"
          >
            <ArrowLeft size={14} />
            {t('backToAlbum')}
          </button>

          <div className="flex items-center gap-2.5 px-4 py-2 bg-[#1e293b]/70 border border-slate-800 rounded-lg shadow-inner self-end">
            <Package size={16} className="text-blue-400" />
            <span className="text-xs font-mono text-slate-400">{t('yourInventory')}</span>
            <span className="text-sm font-bold font-mono text-white bg-blue-600/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded leading-none shrink-0 border-solid">
              {packs} {t('packsCount')}
            </span>
          </div>
        </div>

        {/* Hero Title */}
        <div className="text-center mb-12">
          <p className="text-[10px] font-mono font-bold tracking-[0.4em] text-blue-500 uppercase mb-3">
            {t('getMorePacks')}
          </p>
          <h1 className="font-impact text-3xl md:text-5xl tracking-tight text-white uppercase">
            {t('boutiqueTitle')}
          </h1>
          <p className="max-w-xl mx-auto text-xs text-slate-400 mt-2 font-mono uppercase tracking-wide leading-relaxed">
            {t('boutiqueSubtitle')}
          </p>
        </div>

        {/* Grid Layout of options */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {BUNDLES.map((bundle) => {
            const isHeavy = bundle.id === 'collector' || bundle.id === 'ultimate';
            const translatedBadge = getBundleBadge(bundle.id, bundle.badge);
            const translatedName = getBundleName(bundle.id, bundle.name);
            return (
              <motion.div
                key={bundle.id}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.2 }}
                className={`relative flex flex-col rounded-xl overflow-hidden border bg-[#1e293b]/70 backdrop-blur-sm transition-all shadow-xl group cursor-default ${
                  bundle.id === 'collector' 
                    ? 'border-violet-500/60 border-2 scale-102 shadow-violet-500/10' 
                    : 'border-slate-800 hover:border-slate-700'
                }`}
                style={{
                  boxShadow: `0 10px 30px -10px rgba(15, 23, 42, 0.7), 0 0 20px -5px ${bundle.glowColor}`
                }}
              >
                {/* Popularity/Bonus Badge overlay */}
                {translatedBadge && (
                  <div className={`absolute top-0 left-0 right-0 py-1 text-center text-[8px] font-mono font-black uppercase tracking-widest text-[#0f172a] shadow-sm ${
                    bundle.color === 'sky' ? 'bg-sky-400' :
                    bundle.color === 'emerald' ? 'bg-emerald-400' :
                    bundle.color === 'violet' ? 'bg-violet-400' :
                    bundle.color === 'amber' ? 'bg-amber-400' : 'bg-rose-400'
                  }`}>
                    {translatedBadge}
                  </div>
                )}

                <div className="p-5 pt-8 flex-1 flex flex-col items-center text-center">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${bundle.gradientFrom} ${bundle.gradientTo} text-white shadow-lg mb-4`}>
                    <Package size={26} className="animate-pulse" />
                  </div>

                  <h3 className="font-impact text-lg text-white uppercase tracking-wider mb-1 leading-snug">
                    {translatedName}
                  </h3>
                  
                  {/* Packs count display */}
                  <div className="flex items-baseline gap-1 mt-1 mb-2">
                    <span className="text-3xl font-impact tracking-normal text-white">
                      {bundle.packs}
                    </span>
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider">
                      {t('packs')}
                    </span>
                  </div>

                  <div className="h-px w-10 bg-slate-800 my-2.5" />

                  {/* Specifications checklist */}
                  <ul className="space-y-1.5 text-left w-full text-[10px] font-mono text-slate-400 py-3 self-center max-w-[140px]">
                    <li className="flex items-center gap-1.5 border-solid">
                      <span className="w-1 h-1 rounded-full bg-slate-500" />
                      <span>{bundle.stickersCount} {t('totalInserts')}</span>
                    </li>
                    <li className="flex items-center gap-1.5 border-solid">
                      <span className="w-1 h-1 rounded-full bg-slate-500" />
                      <span>{t('bonus')}: <strong className={bundle.bonusText === '—' ? 'text-slate-500 font-normal' : 'text-emerald-400 font-bold'}>{bundle.bonusText}</strong></span>
                    </li>
                    <li className="flex items-center gap-1.5 border-solid">
                      <span className="w-1 h-1 rounded-full bg-slate-500" />
                      <span>{t('holoGuar')}</span>
                    </li>
                  </ul>
                </div>

                {/* Purchase Button Block */}
                <div className="p-4 border-t border-slate-800/80 bg-slate-900/40">
                  <button
                    onClick={() => {
                      setSelectedBundle(bundle);
                      setPaymentMethod('paypal');
                      setCheckoutStep('form');
                    }}
                    className={`w-full py-2 px-3 rounded text-[11px] font-mono font-black uppercase tracking-wider transition-all cursor-pointer shadow-lg active:scale-[0.98] ${
                      bundle.id === 'collector'
                        ? 'bg-violet-600 text-white hover:bg-violet-500 hover:shadow-violet-600/10'
                        : 'bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    {t('selectFor')} {bundle.price}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Benefits Matrix */}
        <div className="mt-14 p-6 rounded-2xl bg-[#1e293b]/40 border border-slate-800/85 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex gap-4">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0 border-solid">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h4 className="text-xs font-mono font-bold tracking-wider text-slate-200 uppercase mb-1">{t('authenticDist')}</h4>
              <p className="text-[10px] font-mono text-slate-500 leading-normal">{t('authenticDistDesc')}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-9 h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0 border-solid">
              <TrendingUp size={18} />
            </div>
            <div>
              <h4 className="text-xs font-mono font-bold tracking-wider text-slate-200 uppercase mb-1">{t('syncSave')}</h4>
              <p className="text-[10px] font-mono text-slate-500 leading-normal">{t('syncSaveDesc')}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 border-solid">
              <Sparkles size={18} />
            </div>
            <div>
              <h4 className="text-xs font-mono font-bold tracking-wider text-slate-200 uppercase mb-1">{t('instantOpen')}</h4>
              <p className="text-[10px] font-mono text-slate-500 leading-normal">{t('instantOpenDesc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Checkout Drawer/Modal Dialog */}
      <AnimatePresence>
        {selectedBundle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop slide blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={resetCheckout}
            />

            {/* Main Modal body */}
            <motion.div
              initial={{ scale: 0.95, y: 15, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 15, opacity: 0 }}
              className="relative w-full max-w-md bg-[#1e293b]/95 border border-slate-700/80 rounded-xl shadow-2xl overflow-hidden z-20 font-mono"
            >
              {checkoutStep === 'form' && (
                <div className="flex flex-col">
                  {/* Modal Header */}
                  <div className="p-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('secureOrder')}</span>
                    <button
                      onClick={resetCheckout}
                      className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-all cursor-pointer"
                    >
                      <ArrowLeft size={16} />
                    </button>
                  </div>

                  {/* Summary Header */}
                  <div className="p-5.5 bg-gradient-to-r from-slate-900 via-slate-900 to-[#1e293b] border-b border-slate-800/80 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded bg-gradient-to-br ${selectedBundle.gradientFrom} ${selectedBundle.gradientTo} text-white flex items-center justify-center shadow-lg shrink-0`}>
                        <Package size={20} />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white uppercase">{getBundleName(selectedBundle.id, selectedBundle.name)}</h4>
                        <span className="text-[9px] text-slate-400 font-bold block">{selectedBundle.packs} {t('holoPacks')} ({selectedBundle.stickersCount} {t('inserts')})</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs text-slate-500 uppercase block tracking-widest">{t('totalCost')}</span>
                      <span className="text-lg font-impact tracking-wider text-emerald-400">{selectedBundle.price}</span>
                    </div>
                  </div>

                  {checkoutError && (
                    <div className="mx-5.5 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded flex items-start gap-2 text-red-350 text-[10px] leading-relaxed">
                      <span className="text-xs">⚠️</span>
                      <div className="flex-1">
                        <strong className="font-bold">{t('checkoutNotice')} </strong>
                        {checkoutError}
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setCheckoutError(null)} 
                        className="text-slate-400 hover:text-white font-bold text-xs leading-none shrink-0 transition-colors cursor-pointer ml-1"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* Payment Info */}
                  <form onSubmit={handleCheckoutSubmit} className="p-5.5 space-y-4">
                    {/* Payment methods list */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        className={`py-2 px-0.5 rounded border text-[9px] font-bold text-center uppercase tracking-wider transition-all cursor-pointer ${
                          paymentMethod === 'paypal' 
                            ? 'bg-blue-600/10 border-blue-500 text-blue-400' 
                            : 'bg-slate-900/40 border-slate-850 text-slate-500 hover:text-slate-300 hover:border-slate-800'
                        }`}
                      >
                        <div className="italic font-sans font-black text-[10px] lowercase leading-none mb-1">Pay<span className="text-blue-500">Pal</span></div>
                        {t('paypal')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('bitcoin')}
                        className={`py-2 px-1 rounded border text-[9px] font-bold text-center uppercase tracking-wider transition-all cursor-pointer ${
                          paymentMethod === 'bitcoin' 
                            ? 'bg-amber-500/10 border-amber-500 text-amber-500' 
                            : 'bg-slate-900/40 border-slate-850 text-slate-500 hover:text-slate-300 hover:border-slate-800'
                        }`}
                      >
                        <div className="text-amber-500 text-xs font-black leading-none mb-1">₿ Crypto</div>
                        {t('bitcoinCrypto')}
                      </button>
                    </div>

                    {paymentMethod === 'paypal' ? (
                      <div className="space-y-4">
                        <div className="bg-emerald-500/10 border border-emerald-500/20 p-2.5 rounded flex items-center gap-2.5 text-left border-solid">
                          <ShieldCheck size={14} className="text-emerald-400 shrink-0" />
                          <div className="text-[10px] text-emerald-300 font-mono leading-none">
                            <strong>{t('liveGatewayActive')}</strong> {t('apiParamsVerified')}
                          </div>
                        </div>

                        <div className="p-3 bg-[#0f172a] rounded border border-slate-800 text-left space-y-3 border-solid">
                          <span className="text-[9px] font-bold text-slate-500 uppercase block leading-none">
                            {t('paypalSdkApi')}
                          </span>
                          
                          <PayPalButtonWrapper
                            bundle={selectedBundle}
                            onSuccess={(bundle) => {
                                setSavedSuccessBundle(bundle);
                                setCheckoutStep('success');
                            }}
                            onPurchaseComplete={onPurchaseComplete}
                            onError={(msg) => {
                              setCheckoutStep('form');
                              setCheckoutError(msg);
                            }}
                            onProcessing={(status) => {
                              setCheckoutStep('processing');
                              setProcessStatus(status);
                            }}
                            lang={lang}
                          />
                        </div>
                      </div>
                    ) : (
                      /* Bitcoin Cryptographic Web3 Checkout System */
                      <div className="space-y-4 font-mono">
                        {/* Subsystem Mode Toggles */}
                        <div className="flex border border-slate-800 rounded bg-[#0f172a] overflow-hidden p-0.5 border-solid">
                          <button
                            type="button"
                            onClick={() => setBitcoinTransferMode('nowpayments')}
                            className={`flex-1 py-1 text-[8px] font-black uppercase tracking-wider text-center cursor-pointer transition-all rounded ${
                              bitcoinTransferMode === 'nowpayments'
                                ? 'bg-amber-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            NOWPayments
                          </button>
                          <button
                            type="button"
                            onClick={() => setBitcoinTransferMode('bitpay')}
                            className={`flex-1 py-1 text-[8px] font-black uppercase tracking-wider text-center cursor-pointer transition-all rounded ${
                              bitcoinTransferMode === 'bitpay'
                                ? 'bg-amber-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            BitPay
                          </button>
                          <button
                            type="button"
                            onClick={() => setBitcoinTransferMode('qr')}
                            className={`flex-1 py-1 text-[8px] font-black uppercase tracking-wider text-center cursor-pointer transition-all rounded ${
                              bitcoinTransferMode === 'qr'
                                ? 'bg-amber-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            {t('manualQr')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setBitcoinTransferMode('wallet')}
                            className={`flex-1 py-1 text-[8px] font-black uppercase tracking-wider text-center cursor-pointer transition-all rounded ${
                              bitcoinTransferMode === 'wallet'
                                ? 'bg-amber-500 text-slate-950 font-bold'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            {t('web3Wallet')}
                          </button>
                        </div>

                        {bitcoinTransferMode === 'nowpayments' ? (
                          <div className="space-y-3">
                            <div className="bg-amber-500/10 border border-amber-500/20 p-2.5 rounded text-left text-[9px] text-amber-300 leading-normal border-solid">
                              <span className="font-bold block uppercase mb-1">🔗 {t('nowpaymentsCheckout')}</span>
                              <p className="text-[8.5px] leading-relaxed text-slate-400">
                                {t('nowpaymentsDesc')}
                              </p>
                            </div>

                            <div className="bg-[#0f172a] p-3.5 rounded border border-slate-800/80 space-y-3 text-center border-solid">
                              <span className="text-[9px] font-bold text-slate-500 uppercase block leading-none">{t('externalLink')}</span>
                              
                              <div className="flex flex-col items-center justify-center p-4 rounded bg-amber-500/5 border border-amber-500/20 border-solid space-y-3">
                                {!nowpaymentsInvoiceUrl ? (
                                  <button
                                    type="button"
                                    disabled={nowpaymentsLoading}
                                    onClick={async () => {
                                      setNowpaymentsLoading(true);
                                      try {
                                        const response = await fetch('/api/nowpayments/invoice', {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                          },
                                          body: JSON.stringify({
                                            price_amount: selectedBundle.priceNum,
                                            price_currency: 'eur',
                                            order_id: `${selectedBundle.id}_${Date.now()}`,
                                            order_description: `Purchase of ${selectedBundle.packs} Sticker Packs`,
                                            success_url: window.location.href,
                                            cancel_url: window.location.href,
                                          })
                                        });
                                        const data = await response.json();
                                        if (!response.ok) {
                                          throw new Error(data.error || 'Failed to create invoice.');
                                        }
                                        setNowpaymentsInvoiceId(data.id);
                                        setNowpaymentsInvoiceUrl(data.invoice_url);
                                        setNowpaymentsStatus('created');
                                        window.open(data.invoice_url, '_blank', 'noopener,noreferrer');
                                      } catch (err) {
                                        console.error(err);
                                        alert(err instanceof Error ? err.message : 'Unknown error creating invoice');
                                      } finally {
                                        setNowpaymentsLoading(false);
                                      }
                                    }}
                                    className="cursor-pointer py-2 px-4 rounded bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 transition-colors uppercase tracking-wider text-[10px] disabled:opacity-50"
                                  >
                                    {nowpaymentsLoading ? t('processing') : t('nowpaymentsButton')}
                                  </button>
                                ) : (
                                  <div className="w-full space-y-3">
                                    <div className="flex flex-col gap-1.5 items-center">
                                      <a
                                        href={nowpaymentsInvoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded bg-amber-500 text-slate-950 font-black hover:bg-amber-400 transition-all uppercase tracking-wider text-[9px]"
                                      >
                                        🚀 Go to Checkout Page
                                      </a>
                                      <span className="text-[8px] text-slate-500">Invoice ID: {nowpaymentsInvoiceId}</span>
                                    </div>

                                    <div className="h-px bg-slate-800" />

                                    <div className="flex flex-col items-center gap-2">
                                      <button
                                        type="button"
                                        disabled={nowpaymentsLoading}
                                        onClick={async () => {
                                          if (!nowpaymentsInvoiceId) return;
                                          setNowpaymentsLoading(true);
                                          setNowpaymentsStatus('verifying');
                                          try {
                                            const response = await fetch(`/api/nowpayments/payment-status/${nowpaymentsInvoiceId}`);
                                            const paymentsData = await response.json();
                                            console.log("Invoice status response:", paymentsData);
                                            
                                            // Format might list payments in `data` array or as direct object
                                            // Let's check both finished and finished/confirmed
                                            const list = paymentsData.data || paymentsData.requests || [];
                                            const hasFinished = Array.isArray(list) && list.some(
                                              (p: any) => p.payment_status === 'finished' || p.payment_status === 'confirmed'
                                            );

                                            if (hasFinished) {
                                              setNowpaymentsStatus('success');
                                              await onAddPacks(selectedBundle.packs);
                                              setCheckoutStep('success');
                                            } else {
                                              setNowpaymentsStatus('failed');
                                              alert('Payment is still pending on the blockchain. Please verify you completed payment at the checkout link.');
                                            }
                                          } catch (err) {
                                            console.error(err);
                                            alert('Failed to verify status. Please try again.');
                                            setNowpaymentsStatus('failed');
                                          } finally {
                                            setNowpaymentsLoading(false);
                                          }
                                        }}
                                        className="cursor-pointer py-1.5 px-3 rounded bg-[#1e293b] text-amber-400 border border-amber-500/20 border-solid font-bold hover:bg-slate-800 transition-colors uppercase tracking-wider text-[9px]"
                                      >
                                        {nowpaymentsLoading ? t('nowpaymentsVerifying') : '🔍 Verify Payment Status'}
                                      </button>
                                      
                                      {nowpaymentsStatus === 'success' && (
                                        <p className="text-[9px] text-emerald-400 leading-normal font-bold">
                                          {t('nowpaymentsSuccess')}
                                        </p>
                                      )}
                                      
                                      {nowpaymentsStatus === 'verifying' && (
                                        <p className="text-[9px] text-amber-400 leading-normal">
                                          {t('nowpaymentsVerifying')}
                                        </p>
                                      )}

                                      {nowpaymentsStatus === 'failed' && (
                                        <p className="text-[9px] text-rose-400 leading-normal">
                                          {t('nowpaymentsPending')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : bitcoinTransferMode === 'bitpay' ? (
                          <div className="space-y-3">
                            <div className="bg-amber-500/10 border border-amber-500/20 p-2.5 rounded text-left text-[9px] text-amber-300 leading-normal border-solid">
                              <span className="font-bold block uppercase mb-1">🔗 {t('bitpayCheckout')}</span>
                              <p className="text-[8.5px] leading-relaxed text-slate-400">
                                {t('bitpayDesc')}
                              </p>
                            </div>

                            <div className="bg-[#0f172a] p-3.5 rounded border border-slate-800/80 space-y-2 text-center border-solid">
                              <span className="text-[9px] font-bold text-slate-500 uppercase block leading-none">{t('externalLink')}</span>
                              <div className="flex flex-col items-center justify-center">
                                <div className="w-full flex flex-col items-center justify-center p-4 rounded bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/20 border-solid transition-all">
                                  <span className="text-[9px] font-bold text-amber-400/80 uppercase block mb-3">{t('bitpayButton')}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const form = document.createElement('form');
                                      form.action = 'https://test.bitpay.com/checkout';
                                      form.method = 'post';
                                      form.target = '_blank';

                                      const actionInput = document.createElement('input');
                                      actionInput.type = 'hidden';
                                      actionInput.name = 'action';
                                      actionInput.value = 'checkout';
                                      form.appendChild(actionInput);

                                      const posInput = document.createElement('input');
                                      posInput.type = 'hidden';
                                      posInput.name = 'posData';
                                      posInput.value = '';
                                      form.appendChild(posInput);

                                      const notifyInput = document.createElement('input');
                                      notifyInput.type = 'hidden';
                                      notifyInput.name = 'notificationType';
                                      notifyInput.value = 'json';
                                      form.appendChild(notifyInput);

                                      const dataInput = document.createElement('input');
                                      dataInput.type = 'hidden';
                                      dataInput.name = 'data';
                                      dataInput.value = '1Hc0M1pxLnkjfSu9swEMBkAWvHQ5hU10Frvq7cZHW/fRDMMV9w8QETWLgTzxWvjx20d5sDGyFBFiH4Wd9383U59uarZMg4h0Ap2FIYKO8Oz/lbQUjLrTbMRS/4te/OBwnWSavarZNADKBiPQVod9PzeTjuKqjgCJTkS9hiSa2+FMsdYg8ESv3dJXHsPdNGWQ';
                                      form.appendChild(dataInput);

                                      document.body.appendChild(form);
                                      form.submit();
                                      document.body.removeChild(form);
                                    }}
                                    className="cursor-pointer hover:scale-105 transition-transform bg-transparent border-none p-0 inline-flex items-center justify-center focus:outline-none"
                                  >
                                    <img
                                      src="https://test.bitpay.com/cdn/en_US/bp-btn-pay-currencies.svg"
                                      alt="BitPay, the easy way to pay with crypto."
                                      style={{ width: "146px" }}
                                      className="block"
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : bitcoinTransferMode === 'qr' ? (
                          <div className="space-y-3">
                            <div className="bg-amber-500/10 border border-amber-500/20 p-2.5 rounded gap-2.5 text-left text-[9px] text-amber-300 leading-normal border-solid">
                              {t('liveBtc')} <strong className="text-white">1 BTC = 65,430 EUR</strong> • {t('feeIndex')}
                            </div>

                            <div className="flex gap-3 bg-[#0f172a] p-3 rounded border border-slate-800/80 border-solid">
                              {/* Glowing high fidelity CSS QR code */}
                              <div className="relative w-20 h-20 bg-white p-1 rounded-md border border-slate-700/50 flex-shrink-0 overflow-hidden flex items-center justify-center border-solid">
                                <div className="grid grid-cols-6 gap-0.5 w-full h-full opacity-90">
                                  {[...Array(36)].map((_, i) => {
                                    const count = 6;
                                    const r = Math.floor(i / count);
                                    const c = i % count;
                                    const isFinder = (r < 2 && c < 2) || (r < 2 && c >= 4) || (r >= 4 && c < 2);
                                    const isMiddle = isFinder ? false : (i % 3 === 0 || i % 7 === 1);
                                    return (
                                      <div 
                                        key={i} 
                                        className={`${isFinder ? 'bg-slate-900 border border-slate-950' : isMiddle ? 'bg-slate-900' : 'bg-transparent'} rounded-xs border-solid`} 
                                      />
                                    );
                                  })}
                                </div>
                                <div className="absolute inset-x-0 h-0.5 bg-amber-500/80 shadow-[0_0_10px_#f59e0b] top-0 animate-[bounce_2.5s_infinite]" />
                              </div>

                              <div className="flex-1 flex flex-col justify-between text-left">
                                <div className="space-y-1">
                                  <span className="text-[7.5px] font-bold text-slate-500 uppercase block">{t('cryptoPayout')}</span>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[8.5px] text-amber-400 font-bold font-mono break-all bg-slate-950/80 p-1 px-1.5 rounded-md flex-1 border border-slate-850 truncate max-w-[130px] border-solid">
                                      bc1qykn8scw3u27p6yj0x09fk776slgp7e7tcux9q
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText('bc1qykn8scw3u27p6yj0x09fk776slgp7e7tcux9q');
                                        setCopiedAddress(true);
                                        setTimeout(() => setCopiedAddress(false), 2000);
                                      }}
                                      className="py-0.5 px-2 text-[8px] font-black uppercase text-slate-300 hover:text-white bg-slate-900 hover:bg-slate-800 rounded border border-slate-800 transition-colors cursor-pointer border-solid"
                                    >
                                      {copiedAddress ? t('copied') : t('copy')}
                                    </button>
                                  </div>
                                </div>
                                <div className="text-[8px] text-slate-400">
                                  {t('submitExactly')} <strong className="text-white">{(selectedBundle.priceNum / 65430).toFixed(6)} BTC</strong> {t('toBeginBlocks')}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Direct Web3 Connection System */
                          <div className="bg-[#0f172a] p-4 rounded border border-slate-850 text-center py-5 space-y-3 border-solid">
                            {connectingWallet ? (
                              <div className="flex flex-col items-center py-2">
                                <div className="w-5 h-5 border-2 border-slate-600 border-t-amber-500 rounded-full animate-spin mb-2" />
                                <span className="text-[8px] font-bold text-amber-400 uppercase tracking-widest animate-pulse">{t('scanningSignature')}</span>
                              </div>
                            ) : walletConnected ? (
                              <div className="space-y-2 text-left">
                                <div className="flex items-center gap-2 text-emerald-400 font-black text-[9px] bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1.5 rounded border-solid">
                                  <span className="text-xs">🔑</span>
                                  <span>{t('walletConnectedDesc')}</span>
                                </div>
                                <p className="text-[8px] text-slate-500 leading-normal">
                                  {t('walletActive')}
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto text-amber-500 text-sm border-solid">
                                  🪙
                                </div>
                                <div className="space-y-1">
                                  <span className="text-[9px] uppercase font-black text-slate-300 block">{t('web3Title')}</span>
                                  <p className="text-[8px] text-slate-500 max-w-xs mx-auto leading-normal">
                                    {t('web3Desc')}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setConnectingWallet(true);
                                    await new Promise(resolve => setTimeout(resolve, 1000));
                                    setConnectingWallet(false);
                                    setWalletConnected(true);
                                  }}
                                  className="w-full py-1.5 px-3 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-black text-[8px] uppercase tracking-wider border border-amber-500/20 cursor-pointer transition-all border-solid"
                                >
                                  {t('connectWeb3')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="h-px bg-slate-800/80 my-1" />

                    <div className="flex items-start gap-2.5 text-[8px] text-slate-500 leading-normal">
                      <Lock size={12} className="text-emerald-500 shrink-0 mt-0.5" />
                      <span>{t('secNotice')}</span>
                    </div>

                    <div className="pt-2 flex gap-3">
                      <button
                        type="button"
                        onClick={resetCheckout}
                        className="flex-1 py-1.5 rounded bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 text-xs font-bold uppercase cursor-pointer"
                      >
                        {t('cancel')}
                      </button>
                      
                      {/* Only render submit block for non-paypal and non-BitPay/NOWPayments checkout since PayPal & BitPay/NOWPayments have external or dedicated button workflows */}
                      {paymentMethod !== 'paypal' && !(paymentMethod === 'bitcoin' && (bitcoinTransferMode === 'bitpay' || bitcoinTransferMode === 'nowpayments')) && (
                        <button
                          type="submit"
                          disabled={paymentMethod === 'bitcoin' && bitcoinTransferMode === 'wallet' && !walletConnected}
                          className={`flex-1 py-1.5 rounded font-black text-[10px] uppercase cursor-pointer shadow-lg flex items-center justify-center gap-1.5 ${
                            paymentMethod === 'bitcoin' 
                              ? bitcoinTransferMode === 'wallet' && !walletConnected 
                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed shadow-none border border-slate-850'
                                : 'bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-amber-500/10'
                              : 'bg-emerald-600 text-slate-950 hover:bg-emerald-500 shadow-emerald-600/10'
                          }`}
                        >
                          {`${{ en: 'Pay', es: 'Pagar', bs: 'Plati', ro: 'Plătește' }[lang || 'en']} ${(selectedBundle.priceNum / 65430).toFixed(6)} BTC (${selectedBundle.price})`}
                        </button>
                      )}

                      {/* Manual claim trigger for BitPay checkout */}
                      {(paymentMethod === 'bitcoin' && bitcoinTransferMode === 'bitpay') && (
                        <button
                          type="button"
                          onClick={async () => {
                            const activeLang = lang || 'en';
                            const loadingText1 = {
                              en: 'Locating secure external transaction record details...',
                              es: 'Localizando detalles seguros del registro de transacciones externas...',
                              bs: 'Pronalaženje sigurnih detalja o eksternoj transakciji...',
                              ro: 'Se caută detaliile securizate ale înregistrării tranzacției externe...'
                            }[activeLang];
                            const loadingText2 = {
                              en: 'Matching checkout identifiers against active database nodes...',
                              es: 'Coincidiendo identificadores de pago con nodos activos de la base de datos...',
                              bs: 'Spajanje identifikatora plaćanja sa aktivnim čvorovima baze podataka...',
                              ro: 'Se compară identificatorii de plată cu nodurile active din baza de date...'
                            }[activeLang];

                            setSavedSuccessBundle(selectedBundle);
                            setCheckoutStep('processing');
                            setProcessStatus(loadingText1);
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            setProcessStatus(loadingText2);
                            await new Promise(resolve => setTimeout(resolve, 800));
                            
                            // Successful trigger
                            await onAddPacks(selectedBundle.packs);
                            setCheckoutStep('success');
                          }}
                          className="flex-1 py-1.5 rounded bg-emerald-600 text-slate-950 font-black hover:bg-emerald-500 text-[9px] uppercase cursor-pointer shadow-lg shadow-emerald-500/10"
                        >
                          {t('claimHosted')}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}

              {checkoutStep === 'processing' && (
                <div className="p-10 flex flex-col items-center justify-center text-center space-y-5 py-14">
                  <div className="w-12 h-12 rounded-full border-4 border-slate-800 border-t-emerald-500 animate-spin" />
                  <div>
                    <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest animate-pulse leading-none mb-2">{t('processingHandshake')}</h3>
                    <p className="text-[9px] font-mono text-emerald-400 font-bold transition-all duration-300">
                      {processStatus}
                    </p>
                  </div>
                </div>
              )}

              {checkoutStep === 'success' && savedSuccessBundle && (
                <div className="p-8 flex flex-col items-center justify-center text-center space-y-6 py-12">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center border-solid"
                  >
                    <CheckCircle size={32} className="text-emerald-400 shrink-0" />
                  </motion.div>

                  <div className="space-y-1.5">
                    <h2 className="text-sm font-bold text-white uppercase tracking-wider">{t('paymentAuthorized')}</h2>
                    <p className="text-[10px] text-slate-400 leading-normal max-w-xs mx-auto">
                      {t('congratsDecks')} <strong className="text-white bg-slate-900 border border-slate-850 px-1 rounded border-solid">{savedSuccessBundle.packs} {t('packsCount').toLowerCase()}</strong>. ${{
                        en: "Let's open them inside the main album panel.",
                        es: "Abramos los sobres dentro del panel del álbum principal.",
                        bs: "Otvorimo ih unutar glavnog panela albuma.",
                        ro: "Să le deschidem în interiorul panoului principal de album."
                      }[lang || 'en']}
                    </p>
                  </div>

                  <div className="w-full bg-slate-900 p-3 px-4 border border-slate-850/80 rounded-lg text-left text-[9px] text-slate-500 space-y-1.5 border-solid">
                    <div className="flex justify-between font-bold">
                      <span className="uppercase text-slate-400">{t('txnStatus')}</span>
                      <span className="text-emerald-400">{t('completed')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="uppercase">{t('packInventoryGranted')}</span>
                      <span className="text-white">+{savedSuccessBundle.packs} {t('packs')}</span>
                    </div>
                    <div className="flex justify-between border-solid">
                      <span className="uppercase">{t('authTotal')}</span>
                      <span className="text-white font-bold">{savedSuccessBundle.price}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => { resetCheckout(); onBack(); }}
                    className="w-full py-2.5 rounded bg-blue-600 text-white font-bold hover:bg-blue-500 text-xs uppercase cursor-pointer transition-all flex items-center justify-center gap-1.5"
                  >
                    <span>{t('openNewPacksNow')}</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
